/**
 * Approval-chain authoring — G5-BUILD-A.
 *
 * Workspace-plane surface for naming who approves a gate. Authoring lives here,
 * not in the admin console: the admin plane is a read-only registry, and the
 * working authoring API is workspace-write.
 *
 * RULED, v1:
 *   - ANY_ONE steps ONLY. `ALL` is not offered. Its two failure modes
 *     (MIN_APPROVALS_MISMATCH, UNSATISFIABLE_ALL_STEP) are computed server-side
 *     at write time from the org's live role headcount, which this client
 *     cannot know or predict. A control whose failures the UI cannot explain is
 *     not shippable.
 *   - The UI enforces "exactly one approver target per step". The
 *     ValidationPipe does not: both fields are independently optional and a
 *     step carrying neither returns 201 from the pipe, caught only in the
 *     service.
 *
 * APPROVER-EDITOR-1: named people are chosen from workspace members (name +
 * email → userId). The UUID text field is gone from the authoring path.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  classifyChainWriteError,
  createApprovalChain,
  roleOptionsForSelect,
  approvalStepLabel,
  validateChainInput,
  type ApprovalChain,
  type ApprovalChainStep,
  type ApprovalStepInput,
  type ChainWriteFailure,
  type StepValidationError,
} from './approvalChains.api';

import { request } from '@/lib/api';
import { useWorkspaceStore } from '@/state/workspace.store';
import type { WorkspaceMember } from '@/features/workspaces/workspace.api';

/** Shown when the members GET fails. An empty picker would claim there are none. */
export const MEMBERS_LOAD_ERROR = "Couldn't load workspace members";

function membersFromPayload(payload: unknown): WorkspaceMember[] {
  if (Array.isArray(payload)) return payload as WorkspaceMember[];
  if (payload && typeof payload === 'object') {
    const wrapped = payload as { __zephixInner?: unknown; data?: unknown };
    if (wrapped.__zephixInner !== undefined) {
      return membersFromPayload(wrapped.__zephixInner);
    }
    if (Array.isArray(wrapped.data)) return wrapped.data as WorkspaceMember[];
  }
  throw new Error(MEMBERS_LOAD_ERROR);
}

type TargetKind = 'PERSON' | 'ROLE';

interface DraftStep extends ApprovalStepInput {
  /** Which target the author picked. Drives which input is shown. */
  targetKind: TargetKind;
}

export type ApproverPickOption = {
  userId: string;
  label: string;
  email: string;
  displayName: string;
};

function memberUserId(m: WorkspaceMember): string {
  return String(m.userId || m.user?.id || '').trim();
}

function memberEmail(m: WorkspaceMember): string {
  return String(m.user?.email || m.email || '').trim();
}

function memberDisplayName(m: WorkspaceMember): string {
  const fromParts = [m.user?.firstName, m.user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (fromParts) return fromParts;
  const nested = String(m.user?.name || m.name || '').trim();
  if (nested) return nested;
  return memberEmail(m) || memberUserId(m) || 'Unknown';
}

/** Build picker rows from the workspace members list (active only). */
export function approverOptionsFromMembers(
  members: WorkspaceMember[],
): ApproverPickOption[] {
  const seen = new Set<string>();
  const options: ApproverPickOption[] = [];
  for (const m of members) {
    if (m.status && m.status !== 'active') continue;
    const userId = memberUserId(m);
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
  const email = memberEmail(m);
  const name = memberDisplayName(m);
  const label = !email || name === email ? name || email : `${name} (${email})`;
  options.push({
    userId,
    email,
    displayName: name || email || 'Approver',
    label,
  });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

function emptyStep(index: number): DraftStep {
  return {
    name: `Step ${index + 1}`,
    targetKind: 'ROLE',
    requiredRole: 'ADMIN',
    approvalType: 'ANY_ONE',
  };
}

/** Hydrate a draft row from a loaded chain step (legacy VIEWER must still show). */
function draftFromLoadedStep(step: ApprovalChainStep, index: number): DraftStep {
  if (step.requiredUserId) {
    return {
      name: step.name || `Step ${index + 1}`,
      targetKind: 'PERSON',
      requiredUserId: step.requiredUserId,
      approvalType: step.approvalType === 'ALL' ? 'ALL' : 'ANY_ONE',
    };
  }
  return {
    name: step.name || `Step ${index + 1}`,
    targetKind: 'ROLE',
    requiredRole: step.requiredRole ?? 'ADMIN',
    approvalType: step.approvalType === 'ALL' ? 'ALL' : 'ANY_ONE',
  };
}

/** Preserve the loaded approval type. A new step stays ANY_ONE. */
function labelForDraft(step: DraftStep, options: ApproverPickOption[]): string {
  const person = options.find((o) => o.userId === step.requiredUserId)?.displayName;
  return approvalStepLabel({
    requiredUserId: step.targetKind === 'PERSON' ? step.requiredUserId : null,
    requiredRole: step.targetKind === 'ROLE' ? step.requiredRole : null,
    personName: person,
    storedName: step.name,
  });
}

function toWireStep(step: DraftStep, options: ApproverPickOption[]): ApprovalStepInput {
  return {
    name: labelForDraft(step, options),
    approvalType: step.approvalType === 'ALL' ? 'ALL' : 'ANY_ONE',
    ...(step.targetKind === 'ROLE'
      ? { requiredRole: step.requiredRole }
      : { requiredUserId: step.requiredUserId }),
  };
}

export interface ApprovalChainEditorProps {
  gateDefinitionId: string;
  gateName?: string;
  /** Called with the saved chain so a parent can refresh its own view. */
  onSaved?: (chain: ApprovalChain) => void;
  /**
   * Hydrate from a loaded chain. Legacy `requiredRole` values (e.g. VIEWER)
   * still render in the role select; they are not offered for new picks.
   */
  initialSteps?: ApprovalChainStep[];
  /**
   * When the parent owns the "Approvers saved." ack (so it survives a soft
   * plan refresh), suppress the in-editor copy to avoid a double banner.
   */
  suppressSavedAck?: boolean;
  /** APPROVED gates stay readable. Add step and Save approvers are not offered. */
  hideWriteActions?: boolean;
}

export function ApprovalChainEditor({
  gateDefinitionId,
  gateName,
  onSaved,
  initialSteps,
  suppressSavedAck = false,
  hideWriteActions = false,
}: ApprovalChainEditorProps) {
  // The POST's `x-workspace-id` is attached by the shared client from THIS
  // value. Read it from the same place so any message we render names the
  // workspace the request actually targeted, not one the page computed.
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);

  const [chainName, setChainName] = useState('Default approval');
  const [steps, setSteps] = useState<DraftStep[]>(() =>
    initialSteps && initialSteps.length > 0
      ? initialSteps.map(draftFromLoadedStep)
      : [emptyStep(0)],
  );
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ChainWriteFailure | null>(null);
  const [clientErrors, setClientErrors] = useState<StepValidationError[]>([]);
  const [savedChainId, setSavedChainId] = useState<string | null>(null);
  const [approverOptions, setApproverOptions] = useState<ApproverPickOption[]>(
    [],
  );
  const [membersLoadError, setMembersLoadError] = useState<string | null>(null);
  const [membersReady, setMembersReady] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) {
      setApproverOptions([]);
      setMembersLoadError(null);
      setMembersReady(true);
      return;
    }
    let cancelled = false;
    setMembersLoadError(null);
    setMembersReady(false);
    void request
      .get<unknown>(`/workspaces/${activeWorkspaceId}/members`)
      .then((payload) => {
        if (cancelled) return;
        setApproverOptions(
          approverOptionsFromMembers(membersFromPayload(payload)),
        );
        setMembersReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setApproverOptions([]);
        setMembersLoadError(MEMBERS_LOAD_ERROR);
        setMembersReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId]);

  const errorsByStep = useMemo(() => {
    const map = new Map<number, StepValidationError[]>();
    for (const err of clientErrors) {
      const list = map.get(err.stepIndex) ?? [];
      list.push(err);
      map.set(err.stepIndex, list);
    }
    return map;
  }, [clientErrors]);

  function updateStep(index: number, patch: Partial<DraftStep>) {
    setSteps((prev) =>
      prev.map((step, i) => (i === index ? { ...step, ...patch } : step)),
    );
  }

  function setTargetKind(index: number, kind: TargetKind) {
    // Clear the other target so a step can never carry both.
    updateStep(index, {
      targetKind: kind,
      requiredRole: kind === 'ROLE' ? 'ADMIN' : undefined,
      requiredUserId: kind === 'PERSON' ? '' : undefined,
    });
  }

  function addStep() {
    setSteps((prev) => [...prev, emptyStep(prev.length)]);
  }

  function removeStep(index: number) {
    setSteps((prev) => prev.filter((_, i) => i !== index));
    setClientErrors([]);
  }

  async function handleSave() {
    setFailure(null);
    setSavedChainId(null);

    const firstLabel = steps[0] ? labelForDraft(steps[0], approverOptions) : chainName;
    const chainLabel =
      chainName === 'Default approval' || chainName === 'Admin approval'
        ? firstLabel
        : chainName;
    const input = {
      gateDefinitionId,
      name: chainLabel,
      steps: steps.map((step) => toWireStep(step, approverOptions)),
    };

    // Validate before sending. A step-level rejection from the server cannot be
    // attributed to a step, so the only way the author learns which row is
    // wrong is if we check it here first.
    const errors = validateChainInput(input);
    setClientErrors(errors);
    if (errors.length > 0) return;

    setSaving(true);
    try {
      const chain = await createApprovalChain(input);
      setSavedChainId(chain.id);
      toast.success('Approvers saved.');
      onSaved?.(chain);
    } catch (err) {
      setFailure(classifyChainWriteError(err));
    } finally {
      setSaving(false);
    }
  }

  function optionsForStep(requiredUserId: string | undefined): ApproverPickOption[] {
    if (
      requiredUserId &&
      !approverOptions.some((o) => o.userId === requiredUserId)
    ) {
      // Persisted id not in the current member list — still show it so the
      // loaded PERSON target is visible, without rendering a raw UUID field.
      return [
        ...approverOptions,
        {
          userId: requiredUserId,
          email: '',
          displayName: 'Saved approver',
          label: 'Saved approver (not in current member list)',
        },
      ];
    }
    return approverOptions;
  }

  return (
    <section
      className="rounded border border-gray-200 p-4"
      aria-labelledby="chain-editor-heading"
    >
      <h3 id="chain-editor-heading" className="text-base font-semibold">
        Who approves {gateName ? `“${gateName}”` : 'this gate'}
      </h3>
      <p className="mt-1 text-sm text-gray-600">
        Each step is satisfied by any one of its approvers. Steps are approved in
        order.
      </p>

      <div className="mt-4">
        <label
          className="block text-sm font-medium"
          htmlFor="chain-editor-name"
        >
          Chain name
        </label>
        <input
          id="chain-editor-name"
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
          value={
            chainName === 'Default approval' || chainName === 'Admin approval'
              ? (steps[0] ? labelForDraft(steps[0], approverOptions) : chainName)
              : chainName
          }
          onChange={(e) => setChainName(e.target.value)}
        />
      </div>

      <fieldset disabled={hideWriteActions} className="min-w-0 border-0 p-0">
      <ol className="mt-4 space-y-4">
        {steps.map((step, index) => {
          const stepErrors = errorsByStep.get(index) ?? [];
          const errorId = `chain-step-${index}-error`;
          const personOptions = optionsForStep(step.requiredUserId);
          return (
            <li
              key={index}
              className="rounded border border-gray-200 p-3"
              aria-label={`Step ${index + 1}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {labelForDraft(step, personOptions)}
                </span>
                {steps.length > 1 && (
                  <button
                    type="button"
                    className="text-sm text-gray-600 underline"
                    onClick={() => removeStep(index)}
                  >
                    Remove step {index + 1}
                  </button>
                )}
              </div>

              <div className="mt-2">
                <label
                  className="sr-only"
                  htmlFor={`chain-step-${index}-name`}
                >
                  Step name
                </label>
                <input
                  id={`chain-step-${index}-name`}
                  className="sr-only"
                  value={step.name}
                  onChange={(e) => updateStep(index, { name: e.target.value })}
                  aria-describedby={stepErrors.length ? errorId : undefined}
                  aria-invalid={stepErrors.length > 0 || undefined}
                />
              </div>

              <fieldset className="mt-3">
                <legend className="text-sm">Approved by</legend>
                <label className="mr-4 text-sm">
                  <input
                    type="radio"
                    name={`chain-step-${index}-target`}
                    checked={step.targetKind === 'ROLE'}
                    onChange={() => setTargetKind(index, 'ROLE')}
                  />{' '}
                  A role
                </label>
                <label className="text-sm">
                  <input
                    type="radio"
                    name={`chain-step-${index}-target`}
                    checked={step.targetKind === 'PERSON'}
                    onChange={() => setTargetKind(index, 'PERSON')}
                  />{' '}
                  A specific person
                </label>
              </fieldset>

              {step.approvalType === 'ALL' ? (
                <p className="mt-2 text-sm text-gray-700" data-testid={`chain-step-${index}-approval-type`}>
                  Everyone in role must approve
                </p>
              ) : null}

              {step.targetKind === 'ROLE' ? (
                <div className="mt-2">
                  <label
                    className="block text-sm"
                    htmlFor={`chain-step-${index}-role`}
                  >
                    Role
                  </label>
                  <select
                    id={`chain-step-${index}-role`}
                    className="mt-1 rounded border border-gray-300 px-2 py-1"
                    value={step.requiredRole ?? ''}
                    onChange={(e) =>
                      updateStep(index, {
                        requiredRole: e.target.value,
                      })
                    }
                  >
                    {roleOptionsForSelect(step.requiredRole).map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mt-2">
                  {membersLoadError ? (
                    <p className="text-sm text-red-700" role="alert">
                      {membersLoadError}
                    </p>
                  ) : !membersReady ? (
                    <p className="text-sm text-gray-600">
                      Loading workspace members…
                    </p>
                  ) : (
                    <>
                      <label
                        className="block text-sm"
                        htmlFor={`chain-step-${index}-user`}
                      >
                        Approver
                      </label>
                      <select
                        id={`chain-step-${index}-user`}
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                        value={step.requiredUserId ?? ''}
                        onChange={(e) =>
                          updateStep(index, { requiredUserId: e.target.value })
                        }
                        aria-describedby={stepErrors.length ? errorId : undefined}
                        aria-invalid={stepErrors.length > 0 || undefined}
                      >
                        <option value="">Select a person…</option>
                        {personOptions.map((opt) => (
                          <option key={opt.userId} value={opt.userId}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              )}

              {stepErrors.length > 0 && (
                <ul id={errorId} className="mt-2 space-y-1">
                  {stepErrors.map((err, i) => (
                    <li key={i} className="text-sm text-red-700">
                      {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
      </fieldset>

      {hideWriteActions ? (
        <p className="mt-4 text-sm text-gray-600" data-testid="approval-chain-writes-closed">
          This gate is approved, so approvers stay as they are.
        </p>
      ) : (
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1 text-sm"
          onClick={addStep}
        >
          Add step
        </button>
        <button
          type="button"
          className="rounded bg-gray-900 px-3 py-1 text-sm text-white"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save approvers'}
        </button>
      </div>
      )}

      {!suppressSavedAck && savedChainId && (
        <p className="mt-3 text-sm text-green-700" role="status">
          Approvers saved.
        </p>
      )}

      {failure && (
        <div className="mt-3 text-sm" role="alert">
          <ChainFailureMessage
            failure={failure}
            activeWorkspaceId={activeWorkspaceId}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Renders a write failure truthfully.
 *
 * The hard case is STEP_VALIDATION_UNATTRIBUTABLE. The server returns
 * `{code:'VALIDATION_ERROR', message:'Invalid request'}` for any nested
 * step-array failure — no field, no index, no permitted values. We say exactly
 * that. Naming a step here would be a guess presented as a finding.
 */
export function ChainFailureMessage({
  failure,
  activeWorkspaceId,
}: {
  failure: ChainWriteFailure;
  activeWorkspaceId: string | null;
}) {
  switch (failure.outcome) {
    case 'WORKSPACE_MISSING':
      return <p className="text-red-700">{failure.message}</p>;

    case 'NOT_A_MEMBER':
      // Measured: admin elevation requires a real, active membership row. An
      // org admin with no membership in this workspace is denied. Render it as
      // the membership problem it is, with somewhere to go, not as a generic
      // "permission denied".
      return (
        <div className="text-red-700">
          <p>
            You need to be a member of this workspace to change who approves its
            gates. Being an organization admin is not enough on its own — admin
            access applies on top of a workspace membership, not instead of one.
          </p>
          {activeWorkspaceId && (
            <p className="mt-1">
              <Link
                className="underline"
                to={`/workspaces/${activeWorkspaceId}/members`}
              >
                Open workspace members
              </Link>{' '}
              to request or add access.
            </p>
          )}
        </div>
      );

    case 'CHAIN_LOCKED':
      return (
        <div className="text-red-700">
          <p>
            This gate has an approval attempt in progress
            {failure.submissionStatus ? ` (${failure.submissionStatus})` : ''},
            so its approvers cannot be replaced right now. Decide or withdraw
            that attempt first.
          </p>
          {failure.submissionId && (
            <p className="mt-1 text-gray-700">
              Blocking submission: {failure.submissionId}
            </p>
          )}
          <p className="mt-1 text-gray-700">
            Reordering the existing steps is still possible while an attempt is
            in flight; replacing or removing the chain is not.
          </p>
        </div>
      );

    case 'INVALID_APPROVER':
      return (
        <p className="text-red-700">
          {failure.message ||
            'That person is not an active member of this organization.'}
        </p>
      );

    case 'STEP_VALIDATION_UNATTRIBUTABLE':
      return (
        <p className="text-red-700">
          The server rejected one of the steps but did not say which one. Check
          each step’s approver and name, then try again.
        </p>
      );

    case 'FIELD_VALIDATION':
      return <p className="text-red-700">{failure.message}</p>;

    case 'UNKNOWN':
    default:
      return <p className="text-red-700">{failure.message}</p>;
  }
}

export default ApprovalChainEditor;
